import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Sun, Moon, MapPin, Camera, Clock, Cloud, Eye, Wind, 
  LogOut, Menu, X, Compass, Droplets, Thermometer,
  Sunrise, Sunset, ChevronRight, Star, Zap, Calculator,
  Table, Bell, Users, Sparkles, Settings, Layers, Box,
  ZoomIn, ZoomOut, Search, PanelRightClose, PanelRightOpen, Aperture
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';
import { toast } from 'sonner';
import Timeline2D from '../components/Timeline2D';
import MilkyWayPlanner from '../components/MilkyWayPlanner';
import PhotoCalculators from '../components/PhotoCalculators';
import AdvancedWeather from '../components/AdvancedWeather';
import EphemerisTables from '../components/EphemerisTables';
import SmartAlerts from '../components/SmartAlerts';
import SpotDiscovery from '../components/SpotDiscovery';
import AIPrediction from '../components/AIPrediction';
import SunMoonLines from '../components/SunMoonLines';
import SettingsPanel from '../components/SettingsPanel';
import AuroraPlanner from '../components/AuroraPlanner';
import View3DPlanner from '../components/View3DPlanner';
import StarTrailsSimulator from '../components/StarTrailsSimulator';
import ShadowCalculator from '../components/ShadowCalculator';
import EclipsePlanner from '../components/EclipsePlanner';
import FOVCalculator from '../components/FOVCalculator';
import DOFCalculator from '../components/DOFCalculator';
import ARCameraView from '../components/ARCameraView';
import MeteorShowers from '../components/MeteorShowers';
import FindSunMoon from '../components/FindSunMoon';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom marker icon
const createMarkerIcon = () => {
  return L.divIcon({
    className: 'custom-marker-container',
    html: `<div style="width: 20px; height: 20px; background: #F59E0B; border: 3px solid white; border-radius: 50%; box-shadow: 0 0 10px rgba(245, 158, 11, 0.5);"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};

// Language Toggle Component
const LanguageToggle = () => {
  const { language, t, changeLanguage } = useI18n();

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'pl' : 'en';
    changeLanguage(newLang);
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={toggleLanguage}
      className="border-white/10 px-2 min-w-[40px]"
      data-testid="toggle-language"
      title={language === 'en' ? t('dashboard.switchToPolish') : t('dashboard.switchToEnglish')}
    >
      <span className="text-xs font-bold">{language.toUpperCase()}</span>
    </Button>
  );
};

// Theme Toggle Component
const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useI18n();

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={toggleTheme}
      className="border-white/10 px-2"
      data-testid="toggle-theme"
      title={theme === 'dark' ? t('dashboard.switchToLightMode') : t('dashboard.switchToDarkMode')}
    >
      {theme === 'dark' ? (
        <Sun className="w-4 h-4" />
      ) : (
        <Moon className="w-4 h-4" />
      )}
    </Button>
  );
};

// Shot Score Component
const ShotScoreRing = ({ score, size = 120 }) => {
  const { t } = useI18n();
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  
  const getColor = (score) => {
    if (score >= 80) return '#10B981';
    if (score >= 60) return '#F59E0B';
    if (score >= 40) return '#F97316';
    return '#F43F5E';
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#1E293B"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor(score)}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="score-circle"
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-3xl font-bold text-white">{score}</span>
        <span className="text-xs text-slate-400 uppercase tracking-wider">{t('dashboard.score')}</span>
      </div>
    </div>
  );
};

// Map click handler
const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect({ lat: e.latlng.lat, lng: e.latlng.lng });
    }
  });
  return null;
};

// Map zoom control component
const MapZoomControl = ({ zoomIn, zoomOut }) => {
  const map = useMap();
  
  useEffect(() => {
    if (zoomIn) {
      map.zoomIn();
    }
  }, [zoomIn, map]);
  
  useEffect(() => {
    if (zoomOut) {
      map.zoomOut();
    }
  }, [zoomOut, map]);
  
  return null;
};

// Expose map instance for zoom
const MapController = ({ onMapReady }) => {
  const map = useMap();
  useEffect(() => {
    if (onMapReady) onMapReady(map);
  }, [map, onMapReady]);
  return null;
};

// Map updater
const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView([center.lat, center.lng], map.getZoom());
    }
  }, [center, map]);
  return null;
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [location, setLocation] = useState({ lat: 52.2297, lng: 21.0122 });
  const [weather, setWeather] = useState(null);
  const [sunMoon, setSunMoon] = useState(null);
  const [shotScore, setShotScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSunLines, setShowSunLines] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);

  const fetchData = useCallback(async (lat, lng) => {
    setLoading(true);
    try {
      const [weatherRes, sunMoonRes, scoreRes] = await Promise.all([
        axios.post(`${API_URL}/weather`, { latitude: lat, longitude: lng }),
        axios.post(`${API_URL}/sun-moon`, { latitude: lat, longitude: lng }),
        axios.post(`${API_URL}/shot-score`, { latitude: lat, longitude: lng })
      ]);
      
      setWeather(weatherRes.data);
      setSunMoon(sunMoonRes.data);
      setShotScore(scoreRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(t('dashboard.loadLocationError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLoc = { lat: position.coords.latitude, lng: position.coords.longitude };
          setLocation(newLoc);
          fetchData(newLoc.lat, newLoc.lng);
        },
        () => {
          fetchData(location.lat, location.lng);
        }
      );
    } else {
      fetchData(location.lat, location.lng);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLocationSelect = (newLocation) => {
    setLocation(newLocation);
    fetchData(newLocation.lat, newLocation.lng);
  };

  const handleSaveCurrentLocation = async () => {
    const name = window.prompt(t('spots.saveLocationPrompt'));
    if (!name || !name.trim()) return;

    try {
      await axios.post(`${API_URL}/spots`, {
        name: name.trim(),
        latitude: location.lat,
        longitude: location.lng,
      });
      toast.success(t('spots.saveLocationSuccess'));
    } catch (error) {
      toast.error(t('spots.saveLocationError'));
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const currentWeather = weather?.current || {};
  const sunTimes = sunMoon?.sun?.times || {};
  const moonData = sunMoon?.moon || {};

  return (
    <div className={`h-screen flex overflow-hidden ${theme === 'light' ? 'bg-slate-100' : 'bg-slate-950'}`}>
      {/* Settings Panel */}
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
      
      {/* Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            className={`w-[280px] border-r flex flex-col z-20 ${
              theme === 'light' 
                ? 'bg-white border-slate-200' 
                : 'bg-slate-900/50 border-white/10'
            }`}
          >
            <div className={`p-6 border-b ${theme === 'light' ? 'border-slate-200' : 'border-white/10'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg gradient-golden-hour flex items-center justify-center">
                  <Sun className="w-6 h-6 text-black" />
                </div>
                <span className={`font-heading font-semibold text-xl ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>LuminaSpot</span>
              </div>
            </div>

            <nav className="flex-1 p-4 space-y-2">
              <Link to="/dashboard">
                <Button variant="ghost" className="w-full justify-start gap-3 text-amber-500 bg-amber-500/10" data-testid="nav-dashboard">
                  <Compass className="w-5 h-5" />
                  {t('nav.planner')}
                </Button>
              </Link>
              <Link to="/spots">
                <Button variant="ghost" className={`w-full justify-start gap-3 ${theme === 'light' ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100' : 'text-slate-300 hover:text-white hover:bg-white/5'}`} data-testid="nav-spots">
                  <MapPin className="w-5 h-5" />
                  {t('nav.spots')}
                </Button>
              </Link>
              <Link to="/equipment">
                <Button variant="ghost" className={`w-full justify-start gap-3 ${theme === 'light' ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100' : 'text-slate-300 hover:text-white hover:bg-white/5'}`} data-testid="nav-equipment">
                  <Camera className="w-5 h-5" />
                  {t('nav.equipment')}
                </Button>
              </Link>
              
              {/* Settings Button */}
              <Button 
                variant="ghost" 
                onClick={() => setSettingsOpen(true)}
                className={`w-full justify-start gap-3 ${theme === 'light' ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100' : 'text-slate-300 hover:text-white hover:bg-white/5'}`} 
                data-testid="nav-settings"
              >
                <Settings className="w-5 h-5" />
                {t('settings.title')}
              </Button>
            </nav>

            <div className="p-4 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                    <span className="text-amber-500 font-medium">{user?.name?.charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{user?.name}</p>
                    <p className="text-slate-500 text-xs">{user?.email}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-400 hover:text-white" data-testid="logout-btn">
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 bg-slate-900/50 border-b border-white/10 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-slate-400 hover:text-white"
              data-testid="sidebar-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            
            {/* Tab Navigation */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className={theme === 'light' ? 'bg-slate-200' : 'bg-slate-800/50'}>
                <TabsTrigger value="overview" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-xs" data-testid="tab-overview">
                  <Compass className="w-3 h-3 mr-1" />
                  {t('tab.overview')}
                </TabsTrigger>
                <TabsTrigger value="3d" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black text-xs" data-testid="tab-3d">
                  <Box className="w-3 h-3 mr-1" />
                  {t('dashboard.tabThreeDee')}
                </TabsTrigger>
                <TabsTrigger value="astro" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-xs" data-testid="tab-astro">
                  <Star className="w-3 h-3 mr-1" />
                  {t('tab.astro')}
                </TabsTrigger>
                <TabsTrigger value="aurora" className="data-[state=active]:bg-green-500 data-[state=active]:text-black text-xs" data-testid="tab-aurora">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {t('dashboard.tabAurora')}
                </TabsTrigger>
                <TabsTrigger value="ephemeris" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-xs" data-testid="tab-ephemeris">
                  <Table className="w-3 h-3 mr-1" />
                  {t('tab.tables')}
                </TabsTrigger>
                <TabsTrigger value="calculators" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-xs" data-testid="tab-calculators">
                  <Calculator className="w-3 h-3 mr-1" />
                  {t('tab.calculators')}
                </TabsTrigger>
                <TabsTrigger value="discover" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-xs" data-testid="tab-discover">
                  <Users className="w-3 h-3 mr-1" />
                  {t('tab.discover')}
                </TabsTrigger>
                <TabsTrigger value="alerts" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-xs" data-testid="tab-alerts">
                  <Bell className="w-3 h-3 mr-1" />
                  {t('tab.alerts')}
                </TabsTrigger>
                <TabsTrigger value="meteors" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-xs" data-testid="tab-meteors">
                  <Star className="w-3 h-3 mr-1" />
                  {t('dashboard.tabMeteors')}
                </TabsTrigger>
                <TabsTrigger value="trails" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white text-xs" data-testid="tab-trails">
                  <Star className="w-3 h-3 mr-1" />
                  {t('dashboard.tabTrails')}
                </TabsTrigger>
                <TabsTrigger value="shadow" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black text-xs" data-testid="tab-shadow">
                  <Sun className="w-3 h-3 mr-1" />
                  {t('dashboard.tabShadow')}
                </TabsTrigger>
                <TabsTrigger value="eclipse" className="data-[state=active]:bg-red-500 data-[state=active]:text-white text-xs" data-testid="tab-eclipse">
                  <Moon className="w-3 h-3 mr-1" />
                  {t('dashboard.tabEclipse')}
                </TabsTrigger>
                <TabsTrigger value="fov" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black text-xs" data-testid="tab-fov">
                  <Eye className="w-3 h-3 mr-1" />
                  {t('dashboard.tabFov')}
                </TabsTrigger>
                <TabsTrigger value="dof" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-xs" data-testid="tab-dof">
                  <Aperture className="w-3 h-3 mr-1" />
                  {t('dashboard.tabDof')}
                </TabsTrigger>
                <TabsTrigger value="ar" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white text-xs" data-testid="tab-ar">
                  <Camera className="w-3 h-3 mr-1" />
                  {t('dashboard.tabAr')}
                </TabsTrigger>
                <TabsTrigger value="find" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-black text-xs" data-testid="tab-find">
                  <Search className="w-3 h-3 mr-1" />
                  {t('dashboard.tabFind')}
                </TabsTrigger>
                <TabsTrigger value="ai" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-xs" data-testid="tab-ai">
                  <Sparkles className="w-3 h-3 mr-1" />
                  {t('tab.ai')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Sun Lines Toggle */}
            <Button
              variant={showSunLines ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowSunLines(!showSunLines)}
              className={showSunLines ? 'bg-amber-500 text-black' : 'border-white/10'}
              data-testid="toggle-sun-lines"
            >
              <Layers className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">{t('map.sunLines')}</span>
            </Button>
            
            {/* Theme Toggle */}
            <ThemeToggle />
            
            {/* Language Toggle */}
            <LanguageToggle />
            
            {/* Left Panel Toggle - visible indicator */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="border-white/10"
              data-testid="toggle-left-panel"
              title={sidebarOpen ? t('dashboard.hideLeftPanel') : t('dashboard.showLeftPanel')}
            >
              {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
            
            {/* Right Panel Toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRightPanelOpen(!rightPanelOpen)}
              className="border-white/10"
              data-testid="toggle-right-panel"
              title={rightPanelOpen ? t('dashboard.hideRightPanel') : t('dashboard.showRightPanel')}
            >
              {rightPanelOpen ? (
                <PanelRightClose className="w-4 h-4" />
              ) : (
                <PanelRightOpen className="w-4 h-4" />
              )}
            </Button>
            
            <div className={`hidden md:flex items-center gap-2 ${theme === 'light' ? 'text-slate-600' : 'text-slate-400'}`}>
              <MapPin className="w-4 h-4" />
              <span className="font-mono text-xs">
                {location.lat.toFixed(4)}°, {location.lng.toFixed(4)}°
              </span>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveCurrentLocation}
              className="border-white/10"
              data-testid="save-current-location-btn"
              title={t('spots.saveCurrentLocation')}
            >
              <MapPin className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">{t('spots.addSpot')}</span>
            </Button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Map */}
          <div className="flex-1 relative">
            <MapContainer
              center={[location.lat, location.lng]}
              zoom={10}
              className="h-full w-full"
              zoomControl={false}
            >
              <TileLayer
                url={theme === 'light' 
                  ? "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                  : "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                }
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <Marker position={[location.lat, location.lng]} icon={createMarkerIcon()} />
              <MapClickHandler onLocationSelect={handleLocationSelect} />
              <MapUpdater center={location} />
              <MapController onMapReady={setMapInstance} />
              <SunMoonLines 
                latitude={location.lat}
                longitude={location.lng}
                sunTimes={sunTimes}
                visible={showSunLines}
              />
            </MapContainer>
            
            {/* Zoom Controls */}
            <div className="absolute top-4 left-4 z-[1000] flex flex-col gap-1">
              <Button
                variant="outline"
                size="icon"
                onClick={() => mapInstance?.zoomIn()}
                className={`w-8 h-8 ${theme === 'light' ? 'bg-white border-slate-300' : 'bg-slate-900 border-white/20'}`}
                data-testid="map-zoom-in"
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => mapInstance?.zoomOut()}
                className={`w-8 h-8 ${theme === 'light' ? 'bg-white border-slate-300' : 'bg-slate-900 border-white/20'}`}
                data-testid="map-zoom-out"
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
            </div>
            
            <div className={`absolute bottom-4 left-4 rounded-lg px-3 py-2 text-xs z-[1000] ${
              theme === 'light' ? 'bg-white/90 text-slate-700' : 'glass text-slate-300'
            }`}>
              {t('map.clickToSelect')}
            </div>
          </div>

          {/* Timeline - moved to right panel top */}
          
          {/* Right Panel - Scrollable with proper overflow */}
          <AnimatePresence>
            {rightPanelOpen && (
              <motion.div 
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 420, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-slate-900/30 border-l border-white/10 overflow-y-auto overflow-x-hidden flex-shrink-0"
              >
                <div className="w-[420px] p-4 space-y-4 pb-8">
                  {/* Timeline at top of panel */}
                  <Timeline2D 
                    latitude={location.lat} 
                    longitude={location.lng}
                    sunTimes={sunTimes}
                  />
                  
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                    <>
                      {/* Overview Tab */}
                      {activeTab === 'overview' && (
                        <>
                          {/* Shot Score */}
                          <Card className="bg-slate-900/50 border-white/10">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base font-heading flex items-center gap-2">
                                <Zap className="w-4 h-4 text-amber-500" />
                                {t('shotScore.title')}
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="flex items-center gap-4">
                                <ShotScoreRing score={shotScore?.shot_score?.score || 0} size={100} />
                                <div>
                                  <p className="text-lg font-bold text-white mb-1">
                                    {shotScore?.shot_score?.rating || t('common.loading')}
                                  </p>
                                  <ul className="text-xs text-slate-400 space-y-0.5">
                                {shotScore?.shot_score?.factors?.slice(0, 3).map((factor, i) => (
                                  <li key={i}>{factor}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Sun Times */}
                      <Card className="bg-slate-900/50 border-white/10">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base font-heading flex items-center gap-2">
                            <Sun className="w-4 h-4 text-amber-500" />
                            {t('sun.title')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center gap-2">
                              <Sunrise className="w-4 h-4 text-amber-500" />
                              <div>
                                <p className="text-xs text-slate-500">{t('sun.sunrise')}</p>
                                <p className="font-mono text-sm text-white">{sunTimes.sunrise || '--:--'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Sunset className="w-4 h-4 text-orange-500" />
                              <div>
                                <p className="text-xs text-slate-500">{t('sun.sunset')}</p>
                                <p className="font-mono text-sm text-white">{sunTimes.sunset || '--:--'}</p>
                              </div>
                            </div>
                          </div>

                          <div className="p-2 rounded-lg bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/20">
                            <p className="text-xs text-amber-500 mb-1">{t('sun.goldenHour')}</p>
                            <p className="text-xs text-white">
                              AM: {sunTimes.golden_hour_morning?.start} - {sunTimes.golden_hour_morning?.end}
                            </p>
                            <p className="text-xs text-white">
                              PM: {sunTimes.golden_hour_evening?.start} - {sunTimes.golden_hour_evening?.end}
                            </p>
                          </div>
                          
                          <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-500/20">
                            <p className="text-xs text-blue-400 mb-1">{t('sun.blueHour')}</p>
                            <p className="text-xs text-white">
                              AM: {sunTimes.blue_hour_morning?.start} - {sunTimes.blue_hour_morning?.end}
                            </p>
                            <p className="text-xs text-white">
                              PM: {sunTimes.blue_hour_evening?.start} - {sunTimes.blue_hour_evening?.end}
                            </p>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Moon */}
                      <Card className="bg-slate-900/50 border-white/10">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base font-heading flex items-center gap-2">
                            <Moon className="w-4 h-4 text-indigo-400" />
                            {t('moon.title')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-slate-700 to-slate-400 relative overflow-hidden">
                              <div 
                                className="absolute inset-0 bg-slate-900"
                                style={{ 
                                  clipPath: moonData.phase > 0.5 
                                    ? `inset(0 ${(1 - moonData.phase) * 200}% 0 0)` 
                                    : `inset(0 0 0 ${moonData.phase * 200}%)`
                                }}
                              />
                            </div>
                            <div>
                              <p className="text-white font-medium text-sm">{moonData.phase_name || t('common.loading')}</p>
                              <p className="text-slate-400 text-xs">{moonData.illumination || 0}% {t('moon.illuminated')}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Weather */}
                      <AdvancedWeather 
                        weather={weather} 
                        latitude={location.lat} 
                        longitude={location.lng} 
                      />
                    </>
                  )}

                  {/* Astro Tab */}
                  {activeTab === 'astro' && (
                    <MilkyWayPlanner 
                      latitude={location.lat} 
                      longitude={location.lng} 
                    />
                  )}

                  {/* 3D Tab */}
                  {activeTab === '3d' && (
                    <View3DPlanner 
                      latitude={location.lat} 
                      longitude={location.lng}
                      sunTimes={sunTimes}
                    />
                  )}

                  {/* Aurora Tab */}
                  {activeTab === 'aurora' && (
                    <AuroraPlanner 
                      latitude={location.lat} 
                      longitude={location.lng} 
                    />
                  )}

                  {/* Ephemeris Tab */}
                  {activeTab === 'ephemeris' && (
                    <EphemerisTables 
                      latitude={location.lat} 
                      longitude={location.lng} 
                    />
                  )}

                  {/* Calculators Tab */}
                  {activeTab === 'calculators' && (
                    <PhotoCalculators />
                  )}

                  {/* Discover Tab */}
                  {activeTab === 'discover' && (
                    <SpotDiscovery 
                      userLatitude={location.lat}
                      userLongitude={location.lng}
                      onSelectSpot={handleLocationSelect}
                    />
                  )}

                  {/* Alerts Tab */}
                  {activeTab === 'alerts' && (
                    <SmartAlerts 
                      latitude={location.lat}
                      longitude={location.lng}
                      shotScore={shotScore}
                      sunTimes={sunTimes}
                      moonData={moonData}
                    />
                  )}

                  {/* AI Tab */}
                  {activeTab === 'ai' && (
                    <AIPrediction 
                      latitude={location.lat}
                      longitude={location.lng}
                      weather={weather}
                      sunTimes={sunTimes}
                      moonData={moonData}
                      shotScore={shotScore}
                    />
                  )}

                  {/* Meteors Tab */}
                  {activeTab === 'meteors' && (
                    <MeteorShowers 
                      latitude={location.lat}
                      longitude={location.lng}
                    />
                  )}

                  {/* Star Trails Tab */}
                  {activeTab === 'trails' && (
                    <StarTrailsSimulator 
                      latitude={location.lat}
                      longitude={location.lng}
                    />
                  )}

                  {/* Shadow Calculator Tab */}
                  {activeTab === 'shadow' && (
                    <ShadowCalculator 
                      latitude={location.lat}
                      longitude={location.lng}
                      sunTimes={sunTimes}
                    />
                  )}

                  {/* Eclipse Planner Tab */}
                  {activeTab === 'eclipse' && (
                    <EclipsePlanner 
                      latitude={location.lat}
                      longitude={location.lng}
                    />
                  )}

                  {/* FOV Calculator Tab */}
                  {activeTab === 'fov' && (
                    <FOVCalculator 
                      latitude={location.lat}
                      longitude={location.lng}
                    />
                  )}

                  {/* DOF Calculator Tab */}
                  {activeTab === 'dof' && (
                    <DOFCalculator 
                      latitude={location.lat}
                      longitude={location.lng}
                    />
                  )}

                  {/* AR Camera Tab */}
                  {activeTab === 'ar' && (
                    <ARCameraView 
                      latitude={location.lat}
                      longitude={location.lng}
                      sunTimes={sunTimes}
                    />
                  )}

                  {/* Find Tab */}
                  {activeTab === 'find' && (
                    <FindSunMoon 
                      latitude={location.lat}
                      longitude={location.lng}
                      onDateSelect={(date) => {
                        // Could implement date navigation here
                        toast.success(`${t('dashboard.selectedDate')}: ${date.toLocaleString()}`);
                      }}
                    />
                  )}
                </>
              )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
